import { useState } from 'react'

import { Popover } from '@clientbase/clientbase-library'
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded'
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded'
import { Avatar, ButtonBase, Stack, Tooltip, Typography } from '@mui/material'
import { useAuthContext } from 'context/AuthContext'

import { theme } from 'styles/theme'

import AccountSwitcherList, {
  groupSubtitle,
  useShowAccountSwitcher
} from './AccountSwitcherList'
import * as S from './styles'

type SidebarAccountSwitcherProps = {
  isExpanded: boolean
}

/**
 * SC-10999: empresa (ou visão do grupo) atual no topo do menu lateral, para
 * bater o olho e trocar sem abrir o menu da conta. Só aparece para quem tem
 * mais de uma empresa ou algum grupo.
 */
const SidebarAccountSwitcher = ({
  isExpanded
}: SidebarAccountSwitcherProps) => {
  const { user, groupSession, refreshAccounts } = useAuthContext()
  const showSwitcher = useShowAccountSwitcher()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  if (!showSwitcher) return null

  const title = groupSession ? 'Todas as empresas' : user?.name || ''
  const subtitle = groupSession
    ? groupSubtitle(groupSession.name, groupSession.merchants.length)
    : 'Trocar de empresa'

  const avatar = groupSession ? (
    <S.GroupIconWrapper size={32}>
      <GridViewRoundedIcon sx={{ fontSize: 18 }} />
    </S.GroupIconWrapper>
  ) : (
    <Avatar
      variant="rounded"
      src={user?.logo || undefined}
      alt={title}
      sx={{
        width: 32,
        height: 32,
        fontSize: 14,
        fontWeight: 600,
        borderRadius: '8px',
        flexShrink: 0,
        bgcolor: '#fff',
        color: theme.palette.neutral[300],
        border: `1px solid ${theme.palette.neutral[700]}`,
        '& img': { objectFit: 'contain' }
      }}
    >
      {title.trim().charAt(0).toUpperCase() || '?'}
    </Avatar>
  )

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    // A lista gravada no login pode estar velha (convite aceito em outra aba)
    refreshAccounts().catch(() => null)
  }

  return (
    <>
      <Tooltip
        title={isExpanded ? '' : `${title} · trocar de empresa`}
        placement="right"
        arrow
      >
        <ButtonBase
          onClick={handleOpen}
          aria-label="Trocar de empresa"
          sx={{
            // Largura fixa: o menu se ajusta ao conteúdo, e um nome longo sem
            // quebra o alargaria além dos 260px (o texto corta com reticências)
            width: isExpanded ? 236 : 44,
            minWidth: 0,
            mx: isExpanded ? '12px' : 'auto',
            p: isExpanded ? '8px 10px' : '6px',
            display: 'flex',
            justifyContent: isExpanded ? 'space-between' : 'center',
            gap: 1,
            borderRadius: '10px',
            textAlign: 'left',
            bgcolor: '#fff',
            border: `1px solid ${theme.palette.neutral[700]}`,
            '&:hover': { borderColor: theme.palette.neutral[500] }
          }}
        >
          <Stack direction="row" alignItems="center" gap={1.25} minWidth={0}>
            {avatar}
            {isExpanded && (
              <Stack minWidth={0}>
                <Typography variant="smBold" noWrap>
                  {title}
                </Typography>
                <Typography
                  variant="xsLight"
                  color={theme.palette.neutral[400]}
                  noWrap
                >
                  {subtitle}
                </Typography>
              </Stack>
            )}
          </Stack>
          {isExpanded && (
            <UnfoldMoreRoundedIcon
              sx={{ fontSize: 18, color: theme.palette.neutral[400] }}
            />
          )}
        </ButtonBase>
      </Tooltip>

      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ ml: 1, minWidth: '300px' }}
      >
        <AccountSwitcherList onClose={() => setAnchorEl(null)} />
      </Popover>
    </>
  )
}

export default SidebarAccountSwitcher
